import React, { useCallback, useState } from 'react';
import { LayoutChangeEvent, StyleSheet, View } from 'react-native';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import VerticalDotLine from 'shared/components/vxl/ProgressIndicator/StepChecklist/VerticalDotLine/VerticalDotLine';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';

export interface StepProps {
  /**
   * Unique identifier for the stepper state.
   */
  id: string;
  /**
   * Current state of the stepper.
   */
  stepState: 'current' | 'complete' | 'resting' | 'failed';
  /**
   * Color of the progress trail.
   */
  indicator: {
    /**
     * Icon type for the indicator.
     */
    icon: IconKeyType;
    /**
     * Color of the indicator.
     */
    tintColor: string;
  };
  /**
   * Content properties.
   */
  content: {
    /**
     * Description text for the stepper.
     */
    descriptionText: string;
    /**
     * Annotation text for the stepper.
     */
    annotation?: string | React.ReactNode;
  };
}
const Step = ({
  step,
  isLastItem,
  isFirst,
  previousStep,
}: {
  step: StepProps;
  isLastItem: boolean;
  isFirst: boolean;
  previousStep: StepProps;
}) => {
  const annotationTextStyle =
    step?.stepState === 'current' || step?.stepState === 'complete'
      ? { color: theme.color.interactive.primary }
      : { color: theme.color.text.tertiary };

  const [contentHeight, setContentHeight] = useState<number>(0);
  const viewHeight = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setContentHeight(height);
  }, []);

  const calculateDotLength = () => {
    return Math.max(3, Math.floor(contentHeight / 10 - 5));
  };

  const descriptionTextStyle =
    step?.stepState === 'resting' ||
    step?.stepState === 'current' ||
    step?.stepState === 'complete'
      ? { color: theme.color.text.primary }
      : { color: theme.color.text.secondary };
  const iconColor = (
    step?.stepState === 'current' || step?.stepState === 'complete'
      ? step?.indicator?.tintColor
      : theme.color.border.primarySolid
  ) as string;
  return (
    <View style={styles.stepHolder}>
      <View style={styles.iconContainer}>
        <Icon iconKey={step?.indicator?.icon} iconColor={iconColor} />
      </View>
      {!isFirst ? (
        <View style={styles.stepperLineUp}>
          <VerticalDotLine
            dotLength={calculateDotLength()}
            backgroundColor={
              (step?.stepState === 'complete' &&
                previousStep?.stepState === 'complete') ||
              (step?.stepState === 'current' &&
                previousStep?.stepState === 'complete')
                ? step.indicator?.tintColor
                : theme.color.border.primarySolid
            }
          />
        </View>
      ) : null}

      {!isLastItem ? (
        <View style={styles.stepperLineDown}>
          <VerticalDotLine
            dotLength={calculateDotLength()}
            backgroundColor={
              step?.stepState === 'complete'
                ? step.indicator?.tintColor
                : theme.color.border.primarySolid
            }
          />
        </View>
      ) : null}
      <View style={styles.stepperTextContent} onLayout={viewHeight}>
        <BodyBaseText
          style={[styles.descriptionText, descriptionTextStyle]}
          numberOfLines={0}
        >
          {step.content.descriptionText}
        </BodyBaseText>

        {typeof step.content?.annotation === 'string' ? (
          <BodySmText
            style={[styles.annotation, annotationTextStyle]}
            numberOfLines={0}
          >
            {step.content.annotation}
          </BodySmText>
        ) : (
          step.content?.annotation
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  annotation: {
    color: theme.color.text.tertiary,
  },
  descriptionText: {
    color: theme.color.text.secondary,
  },
  iconContainer: {
    backgroundColor: theme.color.background.primary,
    borderColor: 'transparent',
    borderRadius: 40,
    position: 'relative',
    zIndex: 2,
  },

  stepHolder: {
    alignItems: 'center',
    flexDirection: 'row',
    flex: 1,
    gap: theme.spacing.m,
    position: 'relative',
  },
  stepperLineDown: {
    alignItems: 'center',
    bottom: 2,
    flex: 1,
    height: 'auto',
    justifyContent: 'center',
    left: 11,
    position: 'absolute',
    zIndex: 1,
  },
  stepperLineUp: {
    alignItems: 'center',
    flex: 1,
    height: 'auto',
    justifyContent: 'center',
    left: 11,
    position: 'absolute',
    top: 2,
    zIndex: 1,
  },
  stepperTextContent: {
    flex: 1,
    paddingBottom: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
});

export default Step;
